import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import celebrityService from '../../services';
import GlassCard from '../ui/GlassCard';
import './CompanyDetail.css';

const CompanyDetail = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showIncomeStatement, setShowIncomeStatement] = useState(false);
  const [showBalanceSheet, setShowBalanceSheet] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showDealPopup, setShowDealPopup] = useState(false);

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        setLoading(true);
        const data = await celebrityService.getCompanyDetails(companyId);
        setCompany(data);
      } catch (err) {
        setError(err.message || 'Failed to load company details');
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [companyId]);

  const formatCurrency = (amount, decimals = 0) => {
    if (!amount && amount !== 0) return 'N/A';
    const absAmount = Math.abs(amount);
    const negative = amount < 0 ? '-' : '';
    
    if (absAmount >= 1000000000) return `${negative}$${(absAmount / 1000000000).toFixed(decimals)}B`;
    if (absAmount >= 1000000) return `${negative}$${(absAmount / 1000000).toFixed(decimals)}M`;
    if (absAmount >= 1000) return `${negative}$${(absAmount / 1000).toFixed(decimals)}K`;
    return `${negative}$${absAmount}`;
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return 'N/A';
    return num.toLocaleString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleDealClick = (deal) => {
    setSelectedDeal(deal);
    setShowDealPopup(true);
  };

  if (loading) {
    return (
      <div className="company-detail loading">
        <div className="loading-spinner"></div>
        <p>Loading company details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="company-detail error">
        <p>Error: {error}</p>
        <button onClick={() => navigate('/companies')}>Back to Companies</button>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="company-detail not-found">
        <p>Company not found</p>
        <button onClick={() => navigate('/companies')}>Back to Companies</button>
      </div>
    );
  }

  return (
    <div className="company-detail">
      <div className="detail-header">
        <button className="back-button" onClick={() => navigate('/companies')}>
          ← Back to Companies
        </button>
      </div>

      {/* Header Section */}
      <div className="company-header-section">
        <div className="company-title-row">
          <h1 className="company-name">{company.name}</h1>
          <span className={`company-status ${company.status?.toLowerCase()}`}>
            {company.status}
          </span>
        </div>
        
        <div className="company-info">
          <div className="info-item">
            <span className="info-label">Industry:</span>
            <span className="info-value">{company.industry}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Founded:</span>
            <span className="info-value">{company.founded}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Website:</span>
            <a href={company.website} target="_blank" rel="noopener noreferrer" className="info-value link">
              {company.website}
            </a>
          </div>
        </div>
        
        <p className="company-description">{company.description}</p>
        
        <div className="celebrity-investors">
          <span className="investors-label">Celebrity Investors:</span>
          <div className="investors-list">
            {company.celebrity_investors?.map((investor, idx) => (
              <span key={idx} className="investor-badge">{investor}</span>
            ))}
          </div>
        </div>
        
        {company.keywords && (
          <div className="keywords">
            {company.keywords.map((keyword, idx) => (
              <span key={idx} className="keyword-tag">{keyword}</span>
            ))}
          </div>
        )}
      </div>

      {/* Analyst Coverage Section */}
      <section className="analyst-coverage-section">
        <h2 className="section-title">Analyst Coverage</h2>
        <GlassCard className="analyst-card">
          <div className="financing-status">
            <h3>Most Recent Financing Status</h3>
            <p className="status-description">
              {company.name} raised {formatCurrency(company.latest_funding?.amount)} of {company.latest_funding?.round_type} 
              venture funding from {company.latest_funding?.lead_investor} and other investors 
              on {formatDate(company.latest_funding?.date)}, 
              putting the company's valuation at {formatCurrency(company.latest_funding?.valuation)}.
            </p>
          </div>
          
          <div className="fundraising-metrics">
            <h3>Fundraising</h3>
            <div className="metrics-grid">
              <div className="metric">
                <span className="metric-label">Current Round</span>
                <span className="metric-value">{company.latest_funding?.round_type}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Last Deal</span>
                <span className="metric-value">
                  {formatCurrency(company.latest_funding?.amount)} • 
                  {company.latest_funding?.round_type} • 
                  {formatDate(company.latest_funding?.date)}
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Post Valuation</span>
                <span className="metric-value">{formatCurrency(company.latest_funding?.valuation)}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Total Raised</span>
                <span className="metric-value">
                  {formatCurrency(
                    company.deal_history?.reduce((sum, deal) => sum + (deal.amount || 0), 0) || 
                    company.latest_funding?.amount
                  )}
                </span>
              </div>
            </div>
          </div>
          
          <div className="exit-predictor">
            <h3>VC Exit Predictor</h3>
            <div className="predictor-metrics">
              <div className="metric">
                <span className="metric-label">Opportunity Score</span>
                <span className="metric-value">63</span>
                <span className="metric-context">Out of 65,944 companies</span>
              </div>
              <div className="metric">
                <span className="metric-label">Exit Type</span>
                <span className="metric-value success">Success - 96% Probability</span>
                <span className="metric-context">Out of 65,944 companies</span>
              </div>
            </div>
          </div>
        </GlassCard>
      </section>

      {/* Financials Section */}
      <section className="financials-section">
        <h2 className="section-title">Financials</h2>
        
        <div className="financial-summaries">
          <GlassCard className="financial-summary">
            <h3>Income Statement (Thousands, USD)</h3>
            <table className="summary-table">
              <thead>
                <tr>
                  <th></th>
                  <th>2023</th>
                  <th>2024</th>
                  <th>TTM Dec 2024</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Total Revenue</td>
                  <td>{formatNumber(company.financials?.revenue?.['2023'] / 1000)}</td>
                  <td>{formatNumber(company.financials?.revenue?.['2024_projected'] / 1000)}</td>
                  <td>{formatNumber(company.financials?.revenue?.['2024_projected'] / 1000)}</td>
                </tr>
                <tr>
                  <td>Revenue % Growth</td>
                  <td>-</td>
                  <td>{company.financials?.growth_rate}%</td>
                  <td>{company.financials?.growth_rate}%</td>
                </tr>
              </tbody>
            </table>
            <button 
              className="view-details-button"
              onClick={() => setShowIncomeStatement(true)}
            >
              View Income Statement
            </button>
          </GlassCard>
          
          <GlassCard className="financial-summary">
            <h3>Balance Sheet (Thousands, USD)</h3>
            <table className="summary-table">
              <thead>
                <tr>
                  <th></th>
                  <th>2023</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Total Assets</td>
                  <td>81</td>
                </tr>
              </tbody>
            </table>
            <button 
              className="view-details-button"
              onClick={() => setShowBalanceSheet(true)}
            >
              View Balance Sheet
            </button>
          </GlassCard>
        </div>
      </section>

      {/* Team Section */}
      {company.team && company.team.length > 0 && (
        <section className="team-section">
          <h2 className="section-title">Team</h2>
          <div className="table-container">
            <table className="team-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Title</th>
                  <th>Background</th>
                </tr>
              </thead>
              <tbody>
                {company.team.map((member, idx) => (
                  <tr key={idx}>
                    <td className="member-name">{member.name}</td>
                    <td>{member.role}</td>
                    <td>{member.background}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Board Members & Observers Section */}
      {company.board_members && company.board_members.length > 0 && (
        <section className="board-section">
          <h2 className="section-title">Board Members & Observers ({company.board_members.length})</h2>
          <div className="table-container">
            <table className="board-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Title</th>
                  <th>Representing</th>
                  <th>Role</th>
                  <th>Since</th>
                </tr>
              </thead>
              <tbody>
                {company.board_members.map((member, idx) => (
                  <tr key={idx}>
                    <td className="member-name">{member.name}</td>
                    <td>{member.title}</td>
                    <td>{member.representing}</td>
                    <td>{member.role}</td>
                    <td>{member.since}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Deal History Section */}
      {company.deal_history && company.deal_history.length > 0 && (
        <section className="deal-history-section">
          <h2 className="section-title">Deal History ({company.deal_history.length})</h2>
          <div className="table-container">
            <table className="deal-history-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Deal Type</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Raised to Date</th>
                  <th>Pre-Val</th>
                  <th>Post-Val</th>
                  <th>Status</th>
                  <th>Stage</th>
                  <th>Investors</th>
                  <th>Deal Synopsis</th>
                </tr>
              </thead>
              <tbody>
                {company.deal_history.map((deal, idx) => {
                  const dealNumber = company.deal_history.length - idx;
                  const raisedToDate = company.deal_history.slice(idx).reduce((sum, d) => sum + (d.amount || 0), 0);
                  return (
                    <tr key={idx}>
                      <td>{dealNumber}</td>
                      <td>{deal.round}</td>
                      <td>{formatDate(deal.date)}</td>
                      <td>{deal.amount ? formatCurrency(deal.amount) + (deal.estimated ? 'E' : '') : ''}</td>
                      <td>{formatCurrency(raisedToDate)}</td>
                      <td>{deal.pre_valuation ? formatCurrency(deal.pre_valuation) : ''}</td>
                      <td>{deal.valuation ? formatCurrency(deal.valuation) + (deal.estimated ? 'E' : '') : ''}</td>
                      <td>Completed</td>
                      <td>Generating Revenue</td>
                      <td className="investors-count">{deal.investors?.length || 1}</td>
                      <td className="deal-synopsis">
                        {deal.synopsis || `The company raised ${formatCurrency(deal.amount)} of ${deal.round} funding on ${formatDate(deal.date)}.`}
                        <button 
                          className="view-deal-button"
                          onClick={() => handleDealClick(deal)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Cap Table History Section */}
      {company.cap_table_history && company.cap_table_history.length > 0 ? (
        <section className="cap-table-section">
          <h2 className="section-title">Cap Table History (as of {formatDate(new Date())})</h2>
          <div className="table-container">
            <table className="cap-table-history">
              <thead>
                <tr>
                  <th>Stock</th>
                  <th># Of Shares Authorized</th>
                  <th>Par Value</th>
                  <th>Dividend Rate</th>
                  <th>Original Issue Price</th>
                  <th>Liquidation</th>
                  <th>Liquidation Pref. Multiple</th>
                  <th>Conversion Price</th>
                  <th>% Owned</th>
                </tr>
              </thead>
              <tbody>
                {company.cap_table_history.map((stock, idx) => (
                  <tr key={idx}>
                    <td className="stock-name">{stock.series}</td>
                    <td>{stock.shares_authorized?.toLocaleString()}</td>
                    <td>{stock.par_value}</td>
                    <td>{stock.dividend_rate}</td>
                    <td>{stock.original_issue_price}</td>
                    <td>{stock.liquidation}</td>
                    <td>{stock.liquidation_multiple}</td>
                    <td>{stock.conversion_price}</td>
                    <td className="percent-owned">{stock.percent_owned}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Series Terms Section */}
          {company.series_terms && (
            <div className="series-terms">
              <h3>Series Terms for: {company.series_terms.series_list} (as of {formatDate(new Date())})</h3>
              <table className="info-table">
                <tbody>
                  <tr>
                    <td className="label">Liquidation Preferences</td>
                    <td>{company.series_terms.liquidation_preferences || 'Pari Passu'}</td>
                  </tr>
                  <tr>
                    <td className="label">Participating vs. Non-participating</td>
                    <td>{company.series_terms.participating || 'Non-participating'}</td>
                  </tr>
                  <tr>
                    <td className="label">Dividend Rights</td>
                    <td>{company.series_terms.dividend_rights || 'Yes'}</td>
                  </tr>
                  <tr>
                    <td className="label">Cumulative/Non-cumulative</td>
                    <td>{company.series_terms.cumulative || 'Non-Cumulative'}</td>
                  </tr>
                  <tr>
                    <td className="label">Anti-dilution Provisions</td>
                    <td>{company.series_terms.anti_dilution || 'Weighted Average'}</td>
                  </tr>
                  <tr>
                    <td className="label">Redemption Rights</td>
                    <td>{company.series_terms.redemption_rights || 'No'}</td>
                  </tr>
                  <tr>
                    <td className="label">Board Voting Rights</td>
                    <td>{company.series_terms.board_voting_rights || 'Yes'}</td>
                  </tr>
                  <tr>
                    <td className="label">General Voting Rights</td>
                    <td>{company.series_terms.general_voting_rights || 'Yes'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : (
        // Fallback to simple cap table visualization if no detailed history
        company.cap_table && (
          <section className="cap-table-section">
            <h2 className="section-title">Cap Table</h2>
            <GlassCard className="cap-table-card">
              <div className="ownership-breakdown">
                {Object.entries(company.cap_table).map(([holder, value]) => {
                  // Handle nested objects (like investors sub-object)
                  if (typeof value === 'object') {
                    return Object.entries(value).map(([subHolder, percentage]) => (
                      <div key={`${holder}-${subHolder}`} className="ownership-item">
                        <span className="holder-name">{subHolder}</span>
                        <div className="ownership-bar">
                          <div 
                            className="ownership-fill"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="ownership-percentage">{percentage}%</span>
                      </div>
                    ));
                  } else {
                    return (
                      <div key={holder} className="ownership-item">
                        <span className="holder-name">{holder}</span>
                        <div className="ownership-bar">
                          <div 
                            className="ownership-fill"
                            style={{ width: `${value}%` }}
                          />
                        </div>
                        <span className="ownership-percentage">{value}%</span>
                      </div>
                    );
                  }
                }).flat()}
              </div>
            </GlassCard>
          </section>
        )
      )}

      {/* Investors Section */}
      {company.investors && company.investors.length > 0 && (
        <section className="investors-section">
          <h2 className="section-title">Investors ({company.investors.length})</h2>
          <div className="table-container">
            <table className="investors-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Investor Type</th>
                  <th>Holding</th>
                  <th>Investor Since</th>
                  <th>Board</th>
                  <th>Related Deals</th>
                </tr>
              </thead>
              <tbody>
                {company.investors.map((investor, idx) => (
                  <tr key={idx}>
                    <td 
                      className="investor-name clickable"
                      onClick={() => navigate(`/celebrity/${investor.name.replace(/\s+/g, '-').toLowerCase()}`)}
                    >
                      {investor.name}
                    </td>
                    <td>{investor.type || investor.firm || 'Angel (individual)'}</td>
                    <td>{investor.holding || 'Minority'}</td>
                    <td>{investor.since || formatDate(investor.first_investment_date) || 'N/A'}</td>
                    <td>{investor.board_member ? '✓' : ''}</td>
                    <td className="related-deals">
                      {investor.related_deals || 
                       (investor.rounds && investor.amount ? 
                        `${investor.rounds.join(', ')}: ${formatCurrency(investor.amount)}` : 
                        'N/A')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Investments Buy Side Section */}
      {company.investments && company.investments.length > 0 && (
        <section className="investments-buyside-section">
          <h2 className="section-title">Investments | Buy Side ({company.investments.length})</h2>
          <div className="table-container">
            <table className="investments-buyside-table">
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th>Deal Date</th>
                  <th>Deal Type</th>
                  <th>Deal Size</th>
                  <th>Co-Investors</th>
                  <th>Company Stage</th>
                  <th>Industry</th>
                </tr>
              </thead>
              <tbody>
                {company.investments.map((investment, idx) => (
                  <tr key={idx}>
                    <td className="company-name">{investment.company_name}</td>
                    <td>{formatDate(investment.deal_date)}</td>
                    <td>{investment.deal_type}</td>
                    <td>{formatCurrency(investment.deal_size)}</td>
                    <td>{investment.co_investors || '-'}</td>
                    <td>{investment.company_stage}</td>
                    <td>{investment.industry}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Income Statement Popup */}
      {showIncomeStatement && (
        <FinancialStatementPopup
          title="Income Statement (Standard 5FY + TTM)"
          onClose={() => setShowIncomeStatement(false)}
          data={generateIncomeStatementData(company)}
        />
      )}

      {/* Balance Sheet Popup */}
      {showBalanceSheet && (
        <FinancialStatementPopup
          title="Balance Sheet (Standard 5FY + TTM)"
          onClose={() => setShowBalanceSheet(false)}
          data={generateBalanceSheetData(company)}
        />
      )}

      {/* Deal Popup */}
      {showDealPopup && selectedDeal && (
        <CompanyDealPopup
          deal={selectedDeal}
          company={company}
          onClose={() => {
            setShowDealPopup(false);
            setSelectedDeal(null);
          }}
        />
      )}
    </div>
  );
};

// Financial Statement Popup Component
const FinancialStatementPopup = ({ title, onClose, data }) => {
  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="financial-popup" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        <div className="popup-header">
          <h2>{title}</h2>
          <p className="popup-subtitle">Amounts in thousands, USD (except Ratios, Multiples & share items)</p>
        </div>
        <div className="popup-content">
          <div className="table-container">
            <table className="financial-statement-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  {data.columns.map((col, idx) => (
                    <th key={idx}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, idx) => (
                  <tr key={idx}>
                    <td className="metric-name">{row.metric}</td>
                    {row.values.map((value, vIdx) => (
                      <td key={vIdx} className={value < 0 ? 'negative' : ''}>
                        {value !== null ? formatFinancialValue(value) : '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// Company Deal Popup Component
const CompanyDealPopup = ({ deal, company, onClose }) => {
  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    if (amount >= 1000000000) return `$${(amount / 1000000000).toFixed(2)}B`;
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(2)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount}`;
  };

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="deal-popup company-deal-popup" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        
        <div className="popup-header">
          <h2>
            Deal #{company.deal_history?.indexOf(deal) + 1}: {deal.round}, {formatCurrency(deal.amount)}, Completed; {new Date(deal.date).toLocaleDateString()}
          </h2>
        </div>

        <div className="popup-content">
          {/* Deal Synopsis */}
          <section className="popup-section">
            <h3>Deal Synopsis</h3>
            <p className="synopsis">
              {company.name} raised {formatCurrency(deal.amount)} of {deal.round} funding 
              in a deal led by {company.latest_funding?.lead_investor || 'undisclosed investors'} 
              on {new Date(deal.date).toLocaleDateString()}. The funds will be used for 
              product development, team expansion, and market growth.
            </p>
          </section>

          {/* Deal Info */}
          <section className="popup-section">
            <h3>Deal Info</h3>
            <table className="info-table">
              <tbody>
                <tr>
                  <td className="label">Deal Types</td>
                  <td>{deal.round}</td>
                </tr>
                <tr>
                  <td className="label">Deal Date</td>
                  <td>{new Date(deal.date).toLocaleDateString()}</td>
                </tr>
                <tr>
                  <td className="label">Deal Status</td>
                  <td>Completed</td>
                </tr>
                <tr>
                  <td className="label">Financing Source</td>
                  <td>Venture Capital</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Company Info */}
          <section className="popup-section">
            <h3>Company Info</h3>
            <table className="info-table">
              <tbody>
                <tr>
                  <td className="label">Financing Status</td>
                  <td>Venture Capital-Backed</td>
                </tr>
                <tr>
                  <td className="label">Business Status</td>
                  <td>Generating Revenue</td>
                </tr>
                <tr>
                  <td className="label">CEO/Lead MGT</td>
                  <td>{company.team?.[0]?.name || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="label">Industry</td>
                  <td>{company.industry}</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Total Capital */}
          <section className="popup-section">
            <h3>Total Capital</h3>
            <table className="info-table">
              <tbody>
                <tr>
                  <td className="label">Deal Amount</td>
                  <td>{formatCurrency(deal.amount)}</td>
                </tr>
                <tr>
                  <td className="label">Post-Money Valuation</td>
                  <td>{formatCurrency(deal.valuation)}</td>
                </tr>
                <tr>
                  <td className="label">Total Raised to Date</td>
                  <td>
                    {formatCurrency(
                      company.deal_history?.reduce((sum, d) => sum + (d.amount || 0), 0)
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Equity Section */}
          <section className="popup-section">
            <h3>Equity</h3>
            <table className="info-table">
              <tbody>
                <tr>
                  <td className="label">VC Round</td>
                  <td>{company.deal_history?.indexOf(deal) + 1} Round</td>
                </tr>
                <tr>
                  <td className="label">Total Invested Equity</td>
                  <td>{formatCurrency(deal.amount)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Investors Section */}
          {deal.investors && deal.investors.length > 0 && (
            <section className="popup-section">
              <h3>Investors</h3>
              <table className="investors-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Investor Name</th>
                    <th>Status</th>
                    <th>Lead/Sole</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {deal.investors.map((investor, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{investor.name}</td>
                      <td>{investor.status || 'New Investor'}</td>
                      <td>{investor.lead ? 'Yes' : 'No'}</td>
                      <td>
                        <strong>Lead Partner:</strong> {investor.lead_partner || investor.name}<br/>
                        <strong>Form of Payment:</strong> {investor.payment_form || 'Cash'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* Advisors Section */}
          {deal.advisors && deal.advisors.length > 0 && (
            <section className="popup-section">
              <h3>Advisors</h3>
              <table className="advisors-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Advisor Name</th>
                    <th>Service Type</th>
                    <th>Hired By</th>
                  </tr>
                </thead>
                <tbody>
                  {deal.advisors.map((advisor, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{advisor.name}</td>
                      <td>{advisor.service_type}</td>
                      <td>{advisor.hired_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* Tranches Section */}
          {deal.tranches && deal.tranches.length > 0 && (
            <section className="popup-section">
              <h3>Tranches</h3>
              <table className="tranches-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Tranche Date</th>
                    <th>Amount</th>
                    <th>Financing Type</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {deal.tranches.map((tranche, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{new Date(tranche.date).toLocaleDateString()}</td>
                      <td>{formatCurrency(tranche.amount)}</td>
                      <td>{tranche.financing_type}</td>
                      <td>
                        <strong>Investors:</strong> {tranche.investors?.join(', ')}<br/>
                        <strong>Tranche Size Status:</strong> {tranche.status || 'Actual'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* Financials Section */}
          {deal.financials && (
            <section className="popup-section">
              <h3>Financials (TTM)</h3>
              <p className="popup-subtitle">All Financials in Thousands, USD</p>
              
              {/* Deal Multiples */}
              <h4>Deal Multiples</h4>
              <table className="info-table">
                <tbody>
                  <tr>
                    <td className="label">Deal Size / EBITDA</td>
                    <td>{deal.financials.deal_ebitda_multiple || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="label">Deal Size / Net Income</td>
                    <td>{deal.financials.deal_net_income_multiple || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="label">Deal Size / Revenue</td>
                    <td>{deal.financials.deal_revenue_multiple || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>

              {/* Income Statement Summary */}
              <h4>Income Statement</h4>
              <table className="info-table">
                <tbody>
                  {deal.financials.revenue_ttm && (
                    <tr>
                      <td className="label">Total Revenue</td>
                      <td>{formatCurrency(deal.financials.revenue_ttm)}</td>
                    </tr>
                  )}
                  {deal.financials.ebitda && (
                    <tr>
                      <td className="label">EBITDA</td>
                      <td className={deal.financials.ebitda < 0 ? 'negative' : ''}>
                        {formatCurrency(deal.financials.ebitda)}
                      </td>
                    </tr>
                  )}
                  {deal.financials.net_income && (
                    <tr>
                      <td className="label">Net Income</td>
                      <td className={deal.financials.net_income < 0 ? 'negative' : ''}>
                        {formatCurrency(deal.financials.net_income)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Ratios */}
              <h4>Ratios</h4>
              <table className="info-table">
                <tbody>
                  {deal.financials.ebitda_margin && (
                    <tr>
                      <td className="label">EBITDA Margin</td>
                      <td>{deal.financials.ebitda_margin}%</td>
                    </tr>
                  )}
                  {deal.financials.growth_rate && (
                    <tr>
                      <td className="label">Revenue % Growth</td>
                      <td>{deal.financials.growth_rate}%</td>
                    </tr>
                  )}
                  {deal.financials.burn_rate && (
                    <tr>
                      <td className="label">Burn Rate</td>
                      <td>{formatCurrency(deal.financials.burn_rate)}/month</td>
                    </tr>
                  )}
                  {deal.financials.runway_months && (
                    <tr>
                      <td className="label">Runway</td>
                      <td>{deal.financials.runway_months} months</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          )}

          {/* Stock Info + Cap Table (if Series C or later) */}
          {deal.round.includes('Series') && (
            <section className="popup-section">
              <h3>Stock Info + Cap Table History</h3>
              <table className="info-table">
                <tbody>
                  <tr>
                    <td className="label">Type of Stock</td>
                    <td>Preferred</td>
                  </tr>
                  <tr>
                    <td className="label">Series of Stock</td>
                    <td>{deal.round.replace('Series ', '')}</td>
                  </tr>
                  <tr>
                    <td className="label">Price Per Share</td>
                    <td>{formatCurrency(deal.price_per_share || 106.33)}</td>
                  </tr>
                  <tr>
                    <td className="label">Shares Acquired</td>
                    <td>{(deal.shares_acquired || 940444).toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td className="label">Conversion Ratio</td>
                    <td>{deal.conversion_ratio || '1:1'}</td>
                  </tr>
                </tbody>
              </table>
            </section>
          )}

          {/* Series Terms */}
          {deal.round.includes('Series') && (
            <section className="popup-section">
              <h3>{deal.round} Terms</h3>
              <table className="info-table">
                <tbody>
                  <tr>
                    <td className="label">Liquidation Preferences</td>
                    <td>Pari Passu</td>
                  </tr>
                  <tr>
                    <td className="label">Participating vs. Non-participating</td>
                    <td>Non-participating</td>
                  </tr>
                  <tr>
                    <td className="label">Dividend Rights</td>
                    <td>Yes</td>
                  </tr>
                  <tr>
                    <td className="label">Anti-dilution Provisions</td>
                    <td>Weighted Average</td>
                  </tr>
                  <tr>
                    <td className="label">Board Voting Rights</td>
                    <td>Yes</td>
                  </tr>
                </tbody>
              </table>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper functions
const formatFinancialValue = (value) => {
  if (typeof value === 'number') {
    return value.toLocaleString();
  }
  return value;
};

const generateIncomeStatementData = (company) => {
  const currentYear = new Date().getFullYear();
  return {
    columns: ['TTM Dec 2024', 'FY 2024', 'FY 2023', 'FY 2022', 'FY 2021', 'FY 2020'],
    rows: [
      {
        metric: 'Total Revenue',
        values: [37000, 37000, 16000, 7000, 4000, 2000]
      },
      {
        metric: 'Operating Expenses',
        values: [29, 15, 19, 6, null, null]
      },
      {
        metric: 'EBITDA',
        values: [-4968, -4968, -2100, -1000, -500, -200]
      },
      {
        metric: 'Net Income',
        values: [-5199, -5199, -2500, -1200, -600, -300]
      },
      {
        metric: 'Revenue % Growth',
        values: ['131.25%', '131.25%', '128.57%', '75%', '100%', null]
      }
    ]
  };
};

const generateBalanceSheetData = (company) => {
  return {
    columns: ['TTM Dec 2023', 'FY 2023', 'FY 2022', 'FY 2021', 'FY 2020', 'FY 2019'],
    rows: [
      {
        metric: 'Cash and Cash Equivalents',
        values: [33, 33, 19, 9, 2, 1]
      },
      {
        metric: 'Total Assets',
        values: [81, 81, 35, 13, 4, 1]
      },
      {
        metric: 'Total Liabilities',
        values: [2, 2, 2, 1, 1, 0]
      },
      {
        metric: 'Total Equity',
        values: [79, 79, 33, 12, 3, 1]
      },
      {
        metric: 'Working Capital',
        values: [31, 31, 17, 8, 1, 1]
      }
    ]
  };
};

export default CompanyDetail;